import { IUserCapabilities } from '@/common/Auth';
import { Brands, ClientId } from '@/common/Policy';
import {
	CPSPSupportRequest,
	SendPhysicianMessagesParams,
	SendPhysicianMessagesResult,
} from '@/common/types';
import { BrandedPolicyDetails, User } from '@/common/Users';
import axios, { AxiosPromise, AxiosResponse } from 'axios';

interface BaseApproval<TCategory extends string> {
	brand: Brands;
	category: TCategory;
	date_requested: string;
	email: string;
	name: string;
	sub: string;
}

export interface ReportsApproval extends BaseApproval<'Reports'> {
	policy_numbers: Array<{
		client_id: ClientId;
		policy_number: string;
	}>;
	reasonForRequest?: string;
	role?: string;
}

export interface RiskResourcesApproval extends BaseApproval<'Risk Resources'> {}

export type Approval = ReportsApproval | RiskResourcesApproval;

export interface ApprovalError {
	body: string;
	headers: Record<string, string>;
	statusCode: number;
}

export interface Invite {
	/** The brand/site on which to register the invitee. */
	brand?: Brands;

	/** Self-explanatory. */
	client_id: string;

	/** ??? */
	client_ids?: string[];

	/** The email of the invitee. */
	email: string;

	/** The timestamp of the invite in long form (using `Date.toString()`). */
	invited: string;

	/** The UUID/sub of the user who created this invite. */
	invited_by: string;

	/** Optional duplicate of `name`; for this reason prefer `name`. */
	invitee?: string;

	/** The permissions to award the invitee. */
	invitee_permissions: Partial<IUserCapabilities>;

	/** A UUID identifying this invite. */
	inviteId: string;

	isOrgAdmin?: boolean;

	/** The full name of the invitee. */
	name: string;

	/** A UUID identifying the organization. */
	org_id: string;

	policies?: Array<{
		client_id?: string;
		policy_number: string;
	}>;

	/** Self-explanatory. */
	policy_number: string;

	/**
	 * The UNIX timestamp assigned at creation of the invite that will be used to determine
	 * whether it has aged out.
	 */
	TTL: number;
}

const Staff = {
	getUsers: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users',
		});
	},

	getUser: async sub => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users?sub=' + sub,
		});
	},

	deleteCognitoUser: async sub => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users?delete_cognito_user=' + sub,
		});
	},

	getInvites: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users?invites=true',
		});
	},

	getInvite: async email => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users?invite_email=' + email,
		});
	},

	async getInviteByInviteId(inviteId: string): Promise<Invite | undefined> {
		const response = (await axios({
			baseURL: process.env.VUE_APP_API_URI,
			method: 'GET',
			params: {
				inviteId,
			},
			skipAuth: true,
			url: '/user-invite',
		})) as AxiosResponse<string | undefined>;
		return response.data ? JSON.parse(response.data) : undefined;
	},

	deleteInvites: async invites => {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users',
			data: {
				cmd: 'delete_invites',
				invites,
			},
		});
	},

	updateInvite: async data => {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users',
			data,
		});
	},

	async updateUser(
		data:
			| {
					cmd: 'delete_invites';
					/** A list of email addresses for invitees. */
					invites: string[];
			  }
			| {
					cmd: 'delete_policy';
					policy: Omit<BrandedPolicyDetails, 'status'>;
					sub: string;
			  }
			| {
					cmd: 'disable' | 'enable';
					sub: string;
			  }
			| {
					cmd: 'new_policy';
					org_id: string;
					policy: Omit<BrandedPolicyDetails, 'status'>;
					sub: string;
			  }
			| {
					cmd: 'saveAuditLogMessage';
					brand: Brands;
					message: string;
					reason: string;
					sub: string;
			  }
			| {
					cmd: 'update';
					user: Partial<User>;
			  },
	) {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/users',
			data,
		});
	},

	getAccounting: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/payments',
		});
	},

	getRecurring: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI4 + '/recurringjob/',
		});
	},

	getEnabledRecurring: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI4 + '/recurringusers/',
		});
	},

	submitRecurringStatus: async data => {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI4 + '/recurringjob/',
			data,
		});
	},

	getOffbaseDocuments: async (policyNumber?: string) => {
		let url: string;
		if (typeof policyNumber === 'undefined') {
			url = process.env.VUE_APP_API_URI + '/offbase';
		} else {
			url = process.env.VUE_APP_API_URI + '/offbase?policy_number=' + policyNumber;
		}

		return axios({
			method: 'GET',
			url,
		});
	},

	getClaims: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/claims',
		});
	},

	getClaim: async claimId => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/claims?claim_id=' + claimId,
		});
	},

	updateClaim: async data => {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/claims',
			data,
		});
	},

	async createCPSPSupportRequest(
		supportRequest: CPSPSupportRequest,
	): Promise<CPSPSupportRequest> {
		supportRequest.Id = (
			await axios({
				baseURL: process.env.VUE_APP_CPSP,
				data: supportRequest,
				headers: {
					OutAuthorization: '40ec7247ab11ff90928ea4b3d3763b8310db1213',
				},
				method: 'POST',
				url: '/OncallPhysician/rest/CreateRequest/NewRequest/',
			})
		).data;
		return supportRequest;
	},

	async readCPSPSupportRequests(): Promise<CPSPSupportRequest[]> {
		return (
			await axios({
				baseURL: process.env.VUE_APP_CPSP,
				headers: {
					OutAuthorization: '40ec7247ab11ff90928ea4b3d3763b8310db1213',
				},
				method: 'GET',
				url: '/OncallPhysician/rest/CreateRequest/ListRequests/',
			})
		).data;
	},

	async getSunlightPolicies(customerId, idToken) {
		return (
			await axios({
				url: "https://fyd0vcef8i.execute-api.us-west-2.amazonaws.com/v1/policies?customerId=" + customerId,
				method: 'GET',
				headers: {
					Authorization: idToken,
				},
			})
		).data;
	},

	async updateCPSPSupportRequest(supportRequest: CPSPSupportRequest): Promise<void> {
		await axios({
			baseURL: process.env.VUE_APP_CPSP,
			data: supportRequest,
			headers: {
				OutAuthorization: '40ec7247ab11ff90928ea4b3d3763b8310db1213',
			},
			method: 'POST',
			url: '/OncallPhysician/rest/CreateRequest/UpdateRequest',
		});
	},

	async deleteCPSPSupportRequest(id: CPSPSupportRequest['Id']): Promise<void> {
		await axios({
			baseURL: process.env.VUE_APP_CPSP,
			headers: {
				OutAuthorization: '40ec7247ab11ff90928ea4b3d3763b8310db1213',
			},
			params: {
				RequestId: id,
			},
			method: 'POST',
			url: '/OncallPhysician/rest/CreateRequest/DeleteRequest',
		});
	},

	getCPSPInvoices: async () => {
		return axios({
			method: 'GET',

			url: process.env.VUE_APP_CPSP_SEND + '/invoices',
			headers: {
				OutAuthorization: '3a5306e2-ea11-47ee-822b-c2066e898ee9',
			},
		});
	},

	getServiceEventsForInvoiceId: async invoiceId => {
		return axios({
			method: 'GET',
			url:
				process.env.VUE_APP_CPSP +
				'/OncallPhysician/rest/CreateInvoice/GetServiceEventsForInvoice/?InvoiceId=' +
				invoiceId,
			headers: {
				OutAuthorization: '3a5306e2-ea11-47ee-822b-c2066e898ee9',
			},
		});
	},

	fetchInvoice: async s3Url => {
		// @ts-ignore
		const response = await axios({
			method: 'GET',
			url: s3Url,
			headers: {
				'Content-Type': 'text/html',
			},
			skipAuth: true,
		});
		return response.data;
	},

	async sendPhysicianMessages(
		args: SendPhysicianMessagesParams,
	): Promise<SendPhysicianMessagesResult> {
		return (
			await axios({
				baseURL: process.env.VUE_APP_CPSP_SEND,
				data: args,
				headers: {
					OutAuthorization: '3a5306e2-ea11-47ee-822b-c2066e898ee9',
				},
				method: 'POST',
				url: '/send',
			})
		).data;
	},

	getSites: async () => {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff/sites',
		});
	},

	getApprovals(): AxiosPromise<Array<Approval | ApprovalError>> {
		return axios({
			baseURL: process.env.VUE_APP_API_STAFF_URI,
			method: 'GET',
			params: {
				approvals: true,
			},
			url: '/staff/users',
		});
	},

	sendApproval(
		approve: boolean,
		sub: string,
		category: 'RISK_MANAGEMENT' | 'REPORTS',
		isDelete = false,
		denialReason = '',
	): AxiosPromise<unknown> {
		return axios({
			baseURL: process.env.VUE_APP_API_STAFF_URI,
			method: 'GET',
			params: {
				approvalCategory: category,
				approve,
				denialReason,
				isDelete,
				user_sub: sub,
			},
			url: '/staff/users',
		});
	},

	deleteUser: async sub => {
		return axios({
			method: 'DELETE',
			url: process.env.VUE_APP_API_STAFF_URI + '/delete-user?sub=' + sub,
		});
	},

	getMigrations() {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-migrations',
		});
	},

	getMigration(accountId) {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-migrations/?accountId=' + accountId,
		});
	},

	updateMigrationPassword(accountId, password) {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-migrations',
			data: {
				cmd: 'updatePassword',
				accountId,
				password,
			},
		});
	},

	updateMigrationUsername(accountId, mmicusername) {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-migrations',
			data: {
				cmd: 'updateUsername',
				accountId,
				mmicusername,
			},
		});
	},

	updateMigrationStatus(accountId, migratestatus) {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-migrations',
			data: {
				cmd: 'updateStatus',
				accountId,
				migratestatus,
			},
		});
	},

	getPolicies() {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-policies',
		});
	},

	getPolicy(productId) {
		return axios({
			method: 'GET',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-policies/?product_id=' + productId,
		});
	},

	updatePolicy(data) {
		return axios({
			method: 'POST',
			url: process.env.VUE_APP_API_STAFF_URI + '/staff-policies',
			data,
		});
	},
};

export default Staff;
